/**
 * Character select and character creation.
 *
 * Resolves with the character the player chose. Everything the form offers is
 * fetched from the server's own option list, so the creator can never present a
 * choice the server would then refuse.
 */

import type { Appearance, CharacterSummary, ClassKey } from '@emberreach/shared';
import { validateCharacterName } from '@emberreach/shared';
import { api, ApiError } from '../api/client.js';

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing element #${id}`);
  return node as T;
}
const show = (n: HTMLElement): void => n.classList.remove('hidden');
const hide = (n: HTMLElement): void => n.classList.add('hidden');

function fillSelect(select: HTMLSelectElement, values: readonly string[], labels?: (v: string) => string): void {
  select.replaceChildren();
  for (const value of values) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = labels ? labels(value) : value.replace(/-/g, ' ');
    select.append(option);
  }
}

function swatches(container: HTMLElement, colours: readonly string[], selected: string,
                  onPick: (colour: string) => void): void {
  container.replaceChildren();
  for (const colour of colours) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'swatch' + (colour === selected ? ' active' : '');
    button.style.background = colour;
    // Colour alone is not a label — a screen reader, or anyone who cannot
    // distinguish two similar tones, gets nothing from a bare swatch.
    button.setAttribute('aria-label', colour);
    button.addEventListener('click', () => {
      for (const other of container.querySelectorAll('.swatch')) other.classList.remove('active');
      button.classList.add('active');
      onPick(colour);
    });
    container.append(button);
  }
}

/** Shows the character screens and resolves once a character is chosen. */
export async function chooseCharacter(onSignOut: () => void): Promise<CharacterSummary> {
  const selectScreen = el('character-select');
  const createScreen = el('character-create');
  const list = el('character-list');
  const selectNotice = el('select-notice');
  const slotCount = el('slot-count');

  const options = await api.characterOptions();

  return new Promise<CharacterSummary>((resolve) => {
    // --- the list ----------------------------------------------------------
    const renderList = async (): Promise<void> => {
      selectNotice.textContent = '';
      let characters: CharacterSummary[] = [];
      try {
        characters = (await api.listCharacters()).characters;
      } catch (err) {
        selectNotice.textContent =
          err instanceof ApiError ? err.message : 'Could not load your characters.';
        selectNotice.classList.add('bad');
        return;
      }

      slotCount.textContent = `${characters.length} of ${options.maxCharacters} slots used`;
      list.replaceChildren();

      if (characters.length === 0) {
        const note = document.createElement('p');
        note.className = 'empty-note';
        note.textContent = 'No characters yet. Create one to step into the Reach.';
        list.append(note);
        return;
      }

      for (const character of characters) {
        const row = document.createElement('div');
        row.style.display = 'flex';
        row.style.gap = '8px';

        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'char-card';

        const chip = document.createElement('span');
        chip.className = 'char-chip';
        // Their own colours, so the list is scannable without reading it.
        chip.style.background =
          `linear-gradient(160deg, ${character.appearance.hairColour} 45%, ${character.appearance.skinTone} 45%)`;

        const who = document.createElement('span');
        who.className = 'who';
        const name = document.createElement('strong');
        name.textContent = character.name;
        const detail = document.createElement('span');
        detail.textContent =
          `Level ${character.level} ${character.className} · ${character.zoneId === 'cinderhold' ? 'Cinderhold' : 'Saltmarch'}`;
        who.append(name, detail);

        card.append(chip, who);
        card.addEventListener('click', () => {
          hide(selectScreen);
          resolve(character);
        });

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'char-delete';
        remove.textContent = '🗑';
        remove.setAttribute('aria-label', `Delete ${character.name}`);
        remove.addEventListener('click', () => {
          // Deleting a character somebody has played for weeks should take more
          // than one tap; the confirmation names them so it cannot be misread.
          const sure = window.confirm(
            `Delete ${character.name}, level ${character.level} ${character.className}?\n\nThis cannot be undone.`
          );
          if (!sure) return;
          void api.deleteCharacter(character.id)
            .then(renderList)
            .catch((err: unknown) => {
              selectNotice.textContent =
                err instanceof ApiError ? err.message : 'Could not delete that character.';
              selectNotice.classList.add('bad');
            });
        });

        row.append(card, remove);
        list.append(row);
      }
    };

    // --- the creator -------------------------------------------------------
    const appearance: Appearance = { ...options.appearance.defaults };
    let classKey: ClassKey = 'warrior';

    const buildCreator = (): void => {
      const picker = el('class-picker');
      picker.replaceChildren();
      for (const spec of options.classes) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = spec.name;
        button.className = spec.key === classKey ? 'active' : '';
        button.addEventListener('click', () => {
          classKey = spec.key;
          for (const other of picker.querySelectorAll('button')) other.classList.remove('active');
          button.classList.add('active');
          el('class-blurb').textContent = spec.blurb;
        });
        picker.append(button);
      }
      el('class-blurb').textContent =
        options.classes.find((c) => c.key === classKey)?.blurb ?? '';

      fillSelect(el<HTMLSelectElement>('char-body'), options.appearance.bodyTypes);
      fillSelect(el<HTMLSelectElement>('char-face'), options.appearance.faces,
        (v) => 'Face ' + v.replace('face-', ''));
      fillSelect(el<HTMLSelectElement>('char-hair'), options.appearance.hair);
      fillSelect(el<HTMLSelectElement>('char-outfit'), options.appearance.outfits);

      el<HTMLSelectElement>('char-body').value = appearance.bodyType;
      el<HTMLSelectElement>('char-face').value = appearance.face;
      el<HTMLSelectElement>('char-hair').value = appearance.hair;
      el<HTMLSelectElement>('char-outfit').value = appearance.outfit;

      swatches(el('hair-colours'), options.appearance.hairColours, appearance.hairColour,
        (c) => { appearance.hairColour = c; });
      swatches(el('skin-tones'), options.appearance.skinTones, appearance.skinTone,
        (c) => { appearance.skinTone = c; });
    };

    el<HTMLSelectElement>('char-body').addEventListener('change', (e) => {
      appearance.bodyType = (e.target as HTMLSelectElement).value as Appearance['bodyType'];
    });
    el<HTMLSelectElement>('char-face').addEventListener('change', (e) => {
      appearance.face = (e.target as HTMLSelectElement).value as Appearance['face'];
    });
    el<HTMLSelectElement>('char-hair').addEventListener('change', (e) => {
      appearance.hair = (e.target as HTMLSelectElement).value as Appearance['hair'];
    });
    el<HTMLSelectElement>('char-outfit').addEventListener('change', (e) => {
      appearance.outfit = (e.target as HTMLSelectElement).value as Appearance['outfit'];
    });

    el('new-character').addEventListener('click', () => {
      buildCreator();
      hide(selectScreen);
      show(createScreen);
    });

    el('create-cancel').addEventListener('click', () => {
      hide(createScreen);
      show(selectScreen);
      void renderList();
    });

    el('select-signout').addEventListener('click', onSignOut);

    el('create-confirm').addEventListener('click', () => {
      const nameInput = el<HTMLInputElement>('char-name');
      const notice = el('create-notice');
      notice.textContent = '';
      notice.classList.remove('bad');

      const problem = validateCharacterName(nameInput.value);
      el('char-name-error').textContent = problem ?? '';
      if (problem !== null) return;

      const button = el<HTMLButtonElement>('create-confirm');
      button.disabled = true;

      void api.createCharacter(nameInput.value.trim(), classKey, appearance)
        .then(({ character }) => {
          hide(createScreen);
          resolve(character);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError) {
            el('char-name-error').textContent = err.fieldError('name') ?? '';
            if (err.fieldError('name') === undefined) {
              notice.textContent = err.message;
              notice.classList.add('bad');
            }
          } else {
            notice.textContent = 'Could not reach the server. Check your connection.';
            notice.classList.add('bad');
          }
        })
        .finally(() => { button.disabled = false; });
    });

    show(selectScreen);
    void renderList();
  });
}
